import React, { useState, useEffect } from 'react';
import { X, CheckCircle, Calendar, RefreshCcw, Info, CheckSquare, Square } from 'lucide-react';
import { apiClient } from '../utils/apiClient';

const DynamicWeekOffModal = ({ 
    isOpen, 
    onClose, 
    employeeId, 
    weekStartDate, 
    initialSelectedDates = [], // These are manual ones from parent
    systemAssignedDates = [],  // These are system ones from parent
    disabledDates = [],        // These are leave/holiday from parent
    weeklyRight = 1,
    firmCode, 
    onRefresh 
}) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [manualDates, setManualDates] = useState([]);
    const [error, setError] = useState(null);

    const getYmd = (date) => {
        if (!date) return "";
        const d = new Date(date);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };
    
    // Calculate week days based on start date
    const weekDays = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(weekStartDate);
        d.setDate(d.getDate() + i);
        return d;
    });

    useEffect(() => {
        if (isOpen && employeeId && weekStartDate) {
            fetchCurrentOverrides();
        }
    }, [isOpen, employeeId, weekStartDate]);

    const fetchCurrentOverrides = async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ 
                employeeId, 
                weekStartDate: getYmd(weekStartDate)
            });
            const res = await apiClient(`payroll/dynamic-week-off-override?${params}`, firmCode);
            if (res.hasOverride) {
                // Return only manual ones for the checkbox state
                setManualDates(res.overrideDays.filter(d => d.type === "Manual").map(d => d.date.split('T')[0]));
            } else {
                setManualDates(initialSelectedDates || []);
            }
        } catch (err) {
            console.error("Fetch overrides error", err);
        } finally {
            setLoading(false);
        }
    };

    const toggleDate = (dateStr) => {
        if (disabledDates.includes(dateStr)) return;
        
        setManualDates(prev => {
            if (prev.includes(dateStr)) {
                return prev.filter(d => d !== dateStr);
            } else {
                if (prev.length >= weeklyRight) {
                    setError(`Haftalık hakkınız (${weeklyRight} gün) dolmuştur. Yeni gün seçmek için mevcut bir günü kaldırmalısınız.`);
                    return prev;
                }
                setError(null);
                return [...prev, dateStr];
            }
        });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await apiClient('payroll/dynamic-week-off-override', {
                method: 'POST',
                body: {
                    employeeId,
                    weekStartDate: getYmd(weekStartDate),
                    selectedDays: manualDates // ONLY MANUAL ONES
                }
            }, firmCode);
            if (onRefresh) onRefresh();
            onClose();
        } catch (err) {
            alert("Kaydedilemedi: " + err.message);
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    const dayNames = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-md rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5 dark:border-slate-800">
                    <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
                            <Calendar size={18} />
                        </div>
                        <div>
                            <h3 className="text-base font-bold text-slate-900 dark:text-white leading-tight">HT Sabitleme</h3>
                            <p className="text-[10px] text-slate-500 dark:text-slate-400">Haftalık tatil planlaması</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="rounded-full p-2 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 transition-colors">
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-10 gap-3">
                            <RefreshCcw className="h-7 w-7 animate-spin text-indigo-500" />
                            <span className="text-xs font-medium text-slate-400">Seçimler yükleniyor...</span>
                        </div>
                    ) : (
                        <div className="space-y-3.5">
                            <div className="rounded-xl bg-indigo-50/50 p-3.5 border border-indigo-100 dark:bg-indigo-900/10 dark:border-indigo-900/30">
                                <div className="flex items-center justify-between mb-1.5">
                                    <div className="flex items-center gap-2">
                                        <Info size={14} className="text-indigo-600 dark:text-indigo-400" />
                                        <span className="text-[9px] font-bold text-indigo-700 dark:text-indigo-300 uppercase tracking-widest text-[10px]">Bilgilendirme</span>
                                    </div>
                                    <span className="text-[9px] font-bold bg-white dark:bg-slate-800 px-1.5 py-0.5 rounded-lg border border-indigo-100 dark:border-indigo-900/50 text-indigo-600 dark:text-indigo-400">
                                        HAK: {weeklyRight} GÜN
                                    </span>
                                </div>
                                <p className="text-[10px] text-slate-600 dark:text-slate-400 leading-relaxed">
                                    <span className="text-emerald-600 font-bold">Manuel</span> seçimler sistem atamalarını ezer.
                                </p>
                            </div>

                            {error && (
                                <div className="rounded-xl bg-orange-50 border border-orange-200 px-4 py-3 flex items-center gap-3 animate-in slide-in-from-top-2 duration-300">
                                    <Info size={16} className="text-orange-600 shrink-0" />
                                    <p className="text-[11px] font-medium text-orange-800">{error}</p>
                                </div>
                            )}

                            <div className="flex flex-wrap gap-2 px-1 py-1">
                                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400 text-[10px] font-bold border border-emerald-100 dark:border-emerald-900/30">
                                    <div className="w-2 h-2 rounded-full bg-emerald-500" /> MANUEL
                                </span>
                                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-sky-50 text-sky-700 dark:bg-sky-900/20 dark:text-sky-400 text-[10px] font-bold border border-sky-200 dark:border-sky-800/50 border-dashed">
                                    <div className="w-2 h-2 rounded-full bg-sky-400" /> SİSTEM
                                </span>
                                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-50 text-slate-500 dark:bg-slate-800 dark:text-slate-400 text-[10px] font-bold border border-slate-200 dark:border-slate-700">
                                    <div className="w-2 h-2 rounded-full bg-slate-300 dark:bg-slate-600" /> ENGELLİ
                                </span>
                            </div>

                            <div className="grid grid-cols-1 gap-3">
                                {weekDays.map((date, idx) => {
                                    const dateStr = getYmd(date);
                                    const isManual = manualDates.includes(dateStr);
                                    const isSystem = systemAssignedDates.includes(dateStr) && !isManual;
                                    const isDisabled = disabledDates.includes(dateStr);

                                    let cardCls = "bg-white dark:bg-slate-800/40 border-slate-200 dark:border-slate-700/50 hover:border-indigo-300 dark:hover:border-indigo-500 hover:shadow-md";
                                    let accentCls = "border-l-transparent";
                                    let badge = null;

                                    if (isDisabled) {
                                        cardCls = "bg-slate-50/50 dark:bg-slate-900/20 border-slate-100 dark:border-slate-800/40 opacity-50 cursor-not-allowed";
                                        accentCls = "border-l-slate-300 dark:border-l-slate-800";
                                        badge = <span className="text-[9px] font-bold bg-slate-200 dark:bg-slate-800 px-2 py-0.5 rounded-full text-slate-500">ENGEL / İZİN</span>;
                                    } else if (isManual) {
                                        cardCls = "bg-emerald-50/30 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30 shadow-sm ring-1 ring-emerald-100 dark:ring-emerald-500/20";
                                        accentCls = "border-l-emerald-500 dark:border-l-emerald-400";
                                        badge = <span className="text-[9px] font-bold bg-emerald-500 text-white px-2 py-0.5 rounded-full flex items-center gap-1"><CheckCircle size={10} /> SEÇİLDİ</span>;
                                    } else if (isSystem) {
                                        cardCls = "bg-sky-50/30 dark:bg-sky-500/10 border-sky-200 dark:border-sky-500/30 border-dashed";
                                        accentCls = "border-l-sky-400";
                                        badge = <span className="text-[9px] font-bold bg-sky-100 dark:bg-sky-900/50 text-sky-600 dark:text-sky-400 px-2 py-0.5 rounded-full">SİSTEM ATAMASI</span>;
                                    }

                                    return (
                                        <button
                                            key={dateStr}
                                            disabled={isDisabled}
                                            onClick={() => toggleDate(dateStr)}
                                            className={`group relative flex items-center justify-between rounded-xl border-y border-r border-l-4 p-3 transition-all duration-200 active:scale-[0.98] ${cardCls} ${accentCls}`}
                                        >
                                            <div className="flex flex-col text-left py-0.5">
                                                <div className="flex items-center gap-2 mb-0.5">
                                                    <span className={`text-[13px] font-bold ${isDisabled ? 'text-slate-400 line-through' : 'text-slate-900 dark:text-white'}`}>
                                                        {dayNames[idx]}
                                                    </span>
                                                    {badge}
                                                </div>
                                                <span className={`text-[11px] ${isDisabled ? 'text-slate-400' : 'text-slate-500 dark:text-slate-400 font-medium'}`}>
                                                    {date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                                </span>
                                            </div>
                                            
                                            <div className="flex items-center gap-3">
                                                {!isDisabled && (
                                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                                        isManual ? 'bg-emerald-500 border-emerald-500 text-white scale-110' : 
                                                        'border-slate-200 dark:border-slate-700 group-hover:border-indigo-400'
                                                    }`}>
                                                        {isManual && <CheckCircle size={12} />}
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/50 px-5 py-4 dark:border-slate-800 dark:bg-slate-900/50">
                    <button onClick={onClose} className="rounded-xl px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                        Vazgeç
                    </button>
                    <button 
                        onClick={handleSave} 
                        disabled={saving || loading}
                        className="flex items-center gap-2 rounded-xl bg-indigo-600 px-6 py-2 text-xs font-bold text-white shadow-xl shadow-indigo-500/20 transition-all hover:bg-indigo-700 active:scale-95 disabled:opacity-50"
                    >
                        {saving ? <RefreshCcw size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                        Kaydet ve Kapat
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DynamicWeekOffModal;
